import { z } from 'zod';
import { READ_RECEIPTS_DISABLED_REASONS } from '../enums/notifications';
import { isoDateTimeSchema, uuidSchema } from './common';

/**
 * Read receipts (D-550, SPEC §4.3), built on the existing read state
 * (`conversation_members.last_read_seq` / `last_read_at`); there are no per-message receipt rows.
 * Owned by the `receipts` workstream (apps/api/src/modules/messaging/receipts*.ts).
 *
 *   GET /conversations/:conversationId/read-receipts
 *
 * Visibility goes through the conversation access policy (`assertCanRead` → 404). `items` lists
 * the **other** active members who share their read position, only when the workspace setting
 * covers the conversation (`readReceiptsVisibility` in @orbit/shared/read-receipts) and the caller
 * shares theirs; otherwise `items` is empty and `disabled` says why. Members who left, the Hub
 * system user and bots never appear.
 */

export const readReceiptDtoSchema = z.object({
  userId: uuidSchema,
  /** Everything up to and including this seq was read (the conversation's own seq, D-015). */
  lastReadSeq: z.number().int().nonnegative(),
  /** When the read position last moved; null for a member who never opened the conversation. */
  lastReadAt: isoDateTimeSchema.nullable(),
});
export type ReadReceiptDto = z.infer<typeof readReceiptDtoSchema>;

export const readReceiptsResponseSchema = z.object({
  items: z.array(readReceiptDtoSchema),
  /** Present (and `items` empty) when receipts do not show here for the caller. */
  disabled: z.enum(READ_RECEIPTS_DISABLED_REASONS).optional(),
});
export type ReadReceiptsResponse = z.infer<typeof readReceiptsResponseSchema>;
